import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button, Grid, Box, Typography, Paper, Checkbox, FormControlLabel, TextField,
    IconButton, InputAdornment, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { loginUser } from '../redux/userRelated/userHandle';
import PremiumToast from '../components/PremiumToast';
import { useToast } from '../context/ToastContext';

const LoginPage = ({ role }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { status, currentUser, response, error, currentRole } = useSelector(state => state.user);

    const [toggle, setToggle] = useState(false);
    const [loader, setLoader] = useState(false);
    const { showToast } = useToast();

    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);

    const handleSubmit = (event) => {
        event.preventDefault();

        const email = event.target.email.value;
        const password = event.target.password.value;

        if (!email || !password) {
            if (!email) setEmailError(true);
            if (!password) setPasswordError(true);
            return;
        }

        setLoader(true);
        dispatch(loginUser({ email, password }, role));
    };

    const handleInputChange = (event) => {
        const { name } = event.target;
        if (name === 'email') setEmailError(false);
        if (name === 'password') setPasswordError(false);
    };

    useEffect(() => {
        if (status === 'success' || currentUser !== null) {
            if (currentRole === 'Admin') {
                navigate('/Admin/dashboard');
            } else if (currentRole === 'Student') {
                navigate('/Student/dashboard');
            } else if (currentRole === 'Teacher') {
                navigate('/Teacher/dashboard');
            } else if (currentRole === 'Parent') {
                navigate('/Parent/dashboard');
            }
        } else if (status === 'failed') {
            showToast(response || 'Login failed. Please try again.', 'error');
            setLoader(false);
        } else if (status === 'error') {
            showToast('Network Error. Please check your connection.', 'error');
            setLoader(false);
        }
    }, [status, currentRole, navigate, error, response, currentUser]);

    return (
        <LoginContainer>
            <Blob1 />
            <Blob2 />
            <GridPattern />

            <BackButton onClick={() => navigate('/')}>
                <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
                Back to Home
            </BackButton>

            <ContentWrapper>
                <GlassCard elevation={0}>
                    <TitleSection>
                        <RoleBadge>
                            {role} Portal
                        </RoleBadge>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, letterSpacing: '-0.02em' }}>
                            Welcome Back
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b' }}>
                            Please enter your credentials to continue.
                        </Typography>
                    </TitleSection>

                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 4 }}>
                        <StyledTextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            error={emailError}
                            helperText={emailError && 'Email is required'}
                            onChange={handleInputChange}
                        />
                        
                        <StyledTextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={toggle ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            error={passwordError}
                            helperText={passwordError && 'Password is required'}
                            onChange={handleInputChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            onClick={() => setToggle(!toggle)}
                                            edge="end"
                                        >
                                            {toggle ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <OptionsRow>
                            <FormControlLabel 
                                control={
                                    <Checkbox 
                                        value="remember" 
                                        color="primary" 
                                        sx={{
                                            color: '#cbd5e1',
                                            '&.Mui-checked': { color: '#0f172a' },
                                        }}
                                    />
                                } 
                                label={<Typography variant="body2" color="#64748b" fontWeight="500">Remember me</Typography>}
                            />
                            <StyledLink to={`/${role}ForgotPassword`}>
                                Forgot password?
                            </StyledLink>
                        </OptionsRow>

                        <PrimaryButton 
                            type="submit" 
                            fullWidth 
                            variant="contained"
                            disabled={loader}
                        >
                            {loader ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Sign In"}
                        </PrimaryButton>
                    </Box>
                </GlassCard>
            </ContentWrapper>
            
            <PremiumToast />
        </LoginContainer>
    );
};

export default LoginPage;

/* ===== ANIMATIONS & STYLED COMPONENTS ===== */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const float1 = keyframes`
    0% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(30px, -50px) rotate(10deg); }
    66% { transform: translate(-20px, 20px) rotate(-5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
`;

const float2 = keyframes`
    0% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(-30px, 40px) rotate(-10deg); }
    66% { transform: translate(20px, -20px) rotate(5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
`;

const LoginContainer = styled.div`
    min-height: 100vh;
    width: 100%;
    background-color: #fafafa;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const GridPattern = styled.div`
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-image: 
        linear-gradient(to right, rgba(99, 102, 241, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
    background-size: 40px 40px;
    z-index: 1;
    pointer-events: none;
    mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
    -webkit-mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
`;

const Blob1 = styled.div`
    position: absolute;
    top: -10%; left: -10%;
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0) 70%);
    border-radius: 50%;
    animation: ${float1} 15s ease-in-out infinite;
    z-index: 0;
    pointer-events: none;
`;

const Blob2 = styled.div`
    position: absolute;
    bottom: -20%; right: -10%;
    width: 800px; height: 800px;
    background: radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, rgba(168, 85, 247, 0) 70%);
    border-radius: 50%;
    animation: ${float2} 20s ease-in-out infinite;
    z-index: 0;
    pointer-events: none;
`;

const ContentWrapper = styled(Box)`
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 10;
    padding: 24px;
`;

const BackButton = styled(Box)`
    position: absolute;
    top: 40px;
    left: 40px;
    display: flex;
    align-items: center;
    color: #64748b;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    z-index: 20;
    transition: all 0.2s ease;
    padding: 8px 16px;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(10px);

    &:hover {
        color: #0f172a;
        background: white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        transform: translateX(-4px);
    }

    @media (max-width: 600px) {
        top: 20px;
        left: 20px;
    }
`;

const GlassCard = styled(Paper)`
    && {
        width: 100%;
        max-width: 440px;
        padding: 48px;
        border-radius: 32px;
        background: rgba(255, 255, 255, 0.85);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.9);
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08);
        animation: ${fadeIn} 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;

        @media (max-width: 600px) {
            padding: 32px 24px;
            border-radius: 24px;
        }
    }
`;

const TitleSection = styled(Box)`
    text-align: left;
    margin-bottom: 8px;
`;

const RoleBadge = styled(Box)`
    display: inline-block;
    padding: 6px 16px;
    background: #f1f5f9;
    color: #475569;
    border-radius: 100px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 24px;
    border: 1px solid #e2e8f0;
`;

const StyledTextField = styled(TextField)`
    & .MuiOutlinedInput-root {
        border-radius: 16px;
        background-color: white;
        transition: all 0.3s ease;
        
        fieldset {
            border-color: #e2e8f0;
            border-width: 1px;
            transition: all 0.2s ease;
        }
        
        &:hover fieldset {
            border-color: #cbd5e1;
        }
        
        &.Mui-focused {
            box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
            
            fieldset {
                border-color: #0f172a !important;
                border-width: 2px;
            }
        }
    }
    
    & .MuiInputLabel-root {
        color: #64748b;
        font-weight: 500;
        
        &.Mui-focused {
            color: #0f172a;
        }
    }
`;

const OptionsRow = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    margin-bottom: 32px;
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: #64748b;
    font-weight: 600;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    
    &:hover {
        color: #0f172a;
        text-decoration: underline;
    }
`;

const PrimaryButton = styled(Button)`
    && {
        background: #0f172a;
        color: white;
        padding: 16px;
        border-radius: 16px;
        font-size: 1.1rem;
        font-weight: 700;
        text-transform: none;
        box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.3);
        transition: all 0.3s ease;
        
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 20px 35px -5px rgba(15, 23, 42, 0.4);
            background: #1e293b;
        }
        
        &:active {
            transform: translateY(0);
        }

        &.Mui-disabled {
            background: #cbd5e1;
            color: white;
            box-shadow: none;
        }
    }
`;
